import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui';

/**
 * Development-only sign-in helper.
 *
 * Renders the seeded demo account so a new developer can get in without digging
 * through `prisma/seed.ts`. Everything below is wrapped in `import.meta.env.DEV`,
 * which Vite replaces with a literal `false` in a production build — the branch,
 * the strings and this whole component are eliminated from the shipped bundle.
 *
 * These are not secrets: they belong to a demo tenant that only exists when the
 * seed is run with `SEED_DEMO=true`, against a local database.
 */

/** Mirrors `SEED_DEMO_EMAIL` / `SEED_DEMO_PASSWORD` in the API seed script. */
const DEMO_EMAIL = import.meta.env.VITE_DEV_LOGIN_EMAIL ?? 'owner@demo.stockpilot.local';
const DEMO_PASSWORD = import.meta.env.VITE_DEV_LOGIN_PASSWORD ?? 'DemoPassw0rd!';

interface DevCredentialsProps {
  /** Fills the sign-in form with the demo account. */
  onUse: (credentials: { email: string; password: string }) => void;
  disabled?: boolean;
}

export function DevCredentials({ onUse, disabled }: DevCredentialsProps) {
  if (!import.meta.env.DEV) return null;

  return (
    <aside
      className="rounded-lg border border-dashed border-amber-400 bg-amber-50/60 p-4 dark:border-amber-700 dark:bg-amber-950/30"
      aria-label="Development sign-in helper"
    >
      <div className="flex items-center gap-2">
        <KeyRound className="size-4 text-amber-700 dark:text-amber-400" aria-hidden />
        <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
          Development build
        </p>
        <span className="ml-auto rounded bg-amber-200 px-1.5 py-0.5 font-mono text-[0.625rem] font-semibold uppercase tracking-wider text-amber-900 dark:bg-amber-800 dark:text-amber-100">
          Dev only
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
        <dt className="text-amber-800/80 dark:text-amber-300/80">email</dt>
        <dd className="select-all break-all text-amber-950 dark:text-amber-100">{DEMO_EMAIL}</dd>
        <dt className="text-amber-800/80 dark:text-amber-300/80">password</dt>
        <dd className="select-all break-all text-amber-950 dark:text-amber-100">{DEMO_PASSWORD}</dd>
      </dl>

      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-3 border-amber-400 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900/40"
        disabled={disabled}
        onClick={() => onUse({ email: DEMO_EMAIL, password: DEMO_PASSWORD })}
      >
        Use this account
      </Button>

      <p className="mt-3 text-xs leading-relaxed text-amber-800/90 dark:text-amber-300/90">
        The demo business exists only after{' '}
        <code className="rounded bg-amber-200/70 px-1 py-0.5 dark:bg-amber-900/60">
          SEED_DEMO=true npm run db:seed --workspace @stockpilot/api
        </code>
        . Override with <code className="font-mono">VITE_DEV_LOGIN_EMAIL</code> and{' '}
        <code className="font-mono">VITE_DEV_LOGIN_PASSWORD</code>.
      </p>
    </aside>
  );
}
